import api from './api';
import { CLOUD_API_URL } from '../config/serverConfig';

function getToken(): string | null {
  try {
    const stored = localStorage.getItem('maestro_auth');
    if (stored) return JSON.parse(stored).accessToken || null;
  } catch {}
  return null;
}

async function fetchAudioAsDataUrl(url: string, body: object): Promise<string | null> {
  const token = getToken();
  if (!token) return null;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

export const supportService = {
  chatWithAgent: (message: string, history?: { role: string; content: string }[], language?: string, userName?: string, timeOfDay?: string) =>
    api.post('/api/support/chat', { message, history, language, userName, timeOfDay }),

  textToSpeech: async (text: string): Promise<string | null> => {
    return fetchAudioAsDataUrl(`${CLOUD_API_URL}/api/support/tts`, { text });
  },

  getTourSteps: (language?: string, userName?: string) => {
    const params = new URLSearchParams();
    if (language && language !== 'en') params.set('language', language);
    if (userName) params.set('userName', userName);
    const qs = params.toString();
    return api.get(`/api/support/tour${qs ? `?${qs}` : ''}`);
  },

  getTourStepAudio: async (stepId: string, language?: string, userName?: string): Promise<string | null> => {
    return fetchAudioAsDataUrl(`${CLOUD_API_URL}/api/support/tour/tts`, { stepId, language, userName });
  },

  // File upload (multipart/form-data → R2)
  uploadFile: async (file: File): Promise<{ url: string; name: string; size: number; type: string }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await api.raw('/api/support/upload', { method: 'POST', body: form });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Upload failed: ${text}`);
    }
    return res.json();
  },

  // Tickets (user-facing)
  createTicket: (data: { subject: string; message: string; contactEmail?: string; senderName?: string; attachments?: { url: string; name: string }[]; platform?: string; app_version?: string }) =>
    api.post('/api/support/tickets', data),
  getTickets: () => api.get('/api/support/tickets'),
  getTicket: (id: string) => api.get(`/api/support/tickets/${id}`),
  replyToTicket: (id: string, message: string) =>
    api.post(`/api/support/tickets/${id}/reply`, { message }),
};

export const adminSupportService = {
  // Queue (open/pending only — legacy)
  getQueue: () => api.get('/api/admin/panel/support/queue'),
  // Grouped by status pipeline (new)
  getGroupedTickets: () => api.get('/api/support/tickets?grouped=true'),
  // Create ticket (admin-initiated, on behalf of user)
  createTicket: (data: { subject: string; message: string; userEmail?: string; userName?: string; category?: string }) =>
    api.post('/api/admin/panel/support/tickets', data),
  // All tickets (for cluster view — includes resolved/closed + categoryOverride + customCategories)
  getAllTickets: () => api.get('/api/admin/panel/support/all'),
  getTicket: (id: string) => api.get(`/api/admin/panel/support/tickets/${id}`),
  replyToTicket: (id: string, message: string, html?: string) =>
    api.post(`/api/admin/panel/support/tickets/${id}/reply`, { message, html }),
  updateStatus: (id: string, status: string, opts?: { note?: string; skip_email?: boolean }) =>
    api.post(`/api/support/tickets/${id}/status`, { status, ...opts }),
  updateStatusLegacy: (id: string, status: string) =>
    api.put(`/api/admin/panel/support/tickets/${id}/status`, { status }),
  setTicketCategory: (id: string, category: string) =>
    api.put(`/api/admin/panel/support/tickets/${id}/category`, { category }),
  getCustomCategories: () => api.get('/api/admin/panel/support/categories'),
  createCategory: (id: string, label: string, icon?: string) =>
    api.post('/api/admin/panel/support/categories', { id, label, icon }),
  deleteCategory: (id: string) => api.delete(`/api/admin/panel/support/categories/${encodeURIComponent(id)}`),
  getUserTickets: (userId: string) => api.get(`/api/admin/panel/support/user/${userId}`),
  getEmailActivity: (limit?: number) => api.get(`/api/admin/panel/support/email-activity?limit=${limit || 25}`),

  // Batch reply
  batchReply: (ticketIds: string[], message: string, html?: string) =>
    api.post('/api/admin/panel/support/batch-reply', { ticketIds, message, html }),
  // Reply templates
  getTemplates: () => api.get('/api/admin/panel/support/templates'),
  saveTemplates: (templates: Array<{ id: string; label: string; body: string }>) =>
    api.put('/api/admin/panel/support/templates', { templates }),

  // Knowledge base
  getKnowledge: () => api.get('/api/admin/panel/support/knowledge'),
  createArticle: (data: { title: string; content: string; category?: string }) =>
    api.post('/api/admin/panel/support/knowledge', data),
  updateArticle: (id: string, data: { title?: string; content?: string; category?: string }) =>
    api.put(`/api/admin/panel/support/knowledge/${id}`, data),
  deleteArticle: (id: string) => api.delete(`/api/admin/panel/support/knowledge/${id}`),

  // Resolution draft
  saveResolutionDraft: (id: string, draft: string) =>
    api.post(`/api/support/tickets/${id}/save-resolution-draft`, { draft }),
  getDraft: (id: string) => api.get(`/api/support/tickets/${id}/draft`),
  deleteDraft: (id: string) => api.delete(`/api/support/tickets/${id}/draft`),

  // AI rules
  getAiRules: () => api.get('/api/support/ai-rules'),
  updateAiRules: (rules_text: string) => api.put('/api/support/ai-rules', { rules_text }),

  // Issues
  getIssues: () => api.get('/api/support/issues'),
  getIssue: (id: string) => api.get(`/api/support/issues/${id}`),
  createIssue: (data: { type: string; title: string; description?: string; cursor_brief?: string; affected_versions?: string; create_from_ticket_id?: string }) =>
    api.post('/api/support/issues', data),
  updateIssue: (id: string, data: Record<string, unknown>) =>
    api.patch(`/api/support/issues/${id}`, data),
  attachTicketToIssue: (issueId: string, ticketId: string) =>
    api.post(`/api/support/issues/${issueId}/attach-ticket`, { ticket_id: ticketId }),
  detachTicketFromIssue: async (issueId: string, ticketId: string) => {
    const res = await api.raw(`/api/support/issues/${issueId}/attach-ticket`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket_id: ticketId }),
    });
    if (!res.ok) throw new Error(`Detach failed: ${res.status}`);
    return res.json();
  },
  getTicketIssues: (ticketId: string) => api.get(`/api/support/tickets/${ticketId}/issues`),
  batchCreateIssues: (ticketId: string, issues: { title: string; type: string; description: string; cursor_brief: string; status?: string; fixed_in_version?: string | null }[]) =>
    api.post('/api/support/issues/batch-create', { ticket_id: ticketId, issues }),
  getIssueCursorContext: (issueId: string) => api.get(`/api/support/issues/${issueId}/cursor-context`),
  resolveIssue: (issueId: string, fixedInVersion: string) =>
    api.post(`/api/support/issues/${issueId}/resolve`, { fixed_in_version: fixedInVersion }),
  draftNotifications: (issueId: string, data: { fixed_in_version?: string; extra_context?: string }) =>
    api.post(`/api/support/issues/${issueId}/draft-notifications`, data),
  sendNotification: (issueId: string, ticketId: string, body: string) =>
    api.post(`/api/support/issues/${issueId}/send-notification`, { ticket_id: ticketId, body }),

  // Cursor fix summary
  saveFixSummary: (ticketId: string, cursor_fix_summary: string) =>
    api.post(`/api/support/tickets/${ticketId}/fix-summary`, { cursor_fix_summary }),

  // AI assist (SSE streaming)
  aiAssistSSE: async (
    data: { ticket_id?: string | null; issue_id?: string; question: string; tech_savvy: boolean; likely_bug: boolean; fix_now?: boolean; guidance?: string; model?: string },
    onProgress: (step: string) => void,
  ): Promise<any> => {
    const token = getToken();
    const res = await fetch(`${CLOUD_API_URL}/api/support/ai-assist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`API ${res.status}: ${text}`);
    }
    const reader = res.body?.getReader();
    if (!reader) throw new Error('No response body');
    const decoder = new TextDecoder();
    let buffer = '';
    let result: any = null;
    let errorMsg: string | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          const payload = line.slice(6);
          try {
            const parsed = JSON.parse(payload);
            if (currentEvent === 'progress' && parsed.step) {
              onProgress(parsed.step);
            } else if (currentEvent === 'result') {
              result = parsed;
            } else if (currentEvent === 'error') {
              errorMsg = parsed.error || 'AI assist failed';
            }
          } catch {}
          currentEvent = '';
        }
      }
    }
    if (errorMsg) throw new Error(errorMsg);
    if (!result) {
      // Stream ended without a result event — attempt DB recovery
      if (data.ticket_id) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const ticket = await api.get(`/api/admin/panel/support/tickets/${data.ticket_id}`);
          const saved = ticket?.ai_last_response;
          const savedAt = ticket?.ai_last_response_at;
          if (saved && savedAt) {
            const ageMs = Date.now() - new Date(savedAt).getTime();
            if (ageMs < 60_000 && saved.reply_acknowledge !== undefined) {
              (saved as any)._recovered = true;
              return saved;
            }
          }
        } catch {}
      }
      throw new Error('No result received from AI assistant');
    }
    return result;
  },

  requestDiagnosticSessionForTicket: (ticketId: string, durationHours = 4) =>
    api.post('/api/admin/diagnostics/sessions/from-ticket', {
      ticket_id: ticketId,
      duration_hours: durationHours,
    }),
  getAdminDiagnosticSession: (sessionId: string) =>
    api.get(`/api/admin/diagnostics/sessions/${encodeURIComponent(sessionId)}`),
  getAdminDiagnosticLogs: (sessionId: string, limit = 300) =>
    api.get(
      `/api/admin/diagnostics/sessions/${encodeURIComponent(sessionId)}/logs?limit=${limit}`,
    ),
};

// AutoSupport admin API
export const autoSupportService = {
  // Response log
  getResponses: (params?: { limit?: number; offset?: number; category?: string; urgency?: string; status?: string }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    if (params?.category) qs.set('category', params.category);
    if (params?.urgency) qs.set('urgency', params.urgency);
    if (params?.status) qs.set('status', params.status);
    const q = qs.toString();
    return api.get(`/api/support/auto/responses${q ? `?${q}` : ''}`);
  },

  // Training rules
  getRules: () => api.get('/api/support/auto/rules'),
  addRule: (rule_text: string, category?: string, source_ticket_id?: string) =>
    api.post('/api/support/auto/rules', { rule_text, category, source_ticket_id }),
  deleteRule: (id: string) => api.delete(`/api/support/auto/rules/${id}`),

  // Instructions
  addInstruction: (ticket_id: string, instruction_text: string) =>
    api.post('/api/support/auto/instructions', { ticket_id, instruction_text }),

  // Known issues
  getKnownIssues: () => api.get('/api/support/auto/known-issues'),
  createKnownIssue: (data: { title: string; description?: string; symptoms?: string; category?: string; affected_platforms?: string[]; affected_versions?: string; workaround?: string; status?: string }) =>
    api.post('/api/support/auto/known-issues', data),
  updateKnownIssue: (id: string, data: Record<string, unknown>) =>
    api.patch(`/api/support/auto/known-issues/${id}`, data),
  deleteKnownIssue: (id: string) => api.delete(`/api/support/auto/known-issues/${id}`),

  // Release notes
  getReleaseNotes: () => api.get('/api/support/auto/release-notes'),
  addReleaseNote: (data: { version: string; release_date: string; platform?: string; highlights?: string[]; bug_fixes?: string[]; known_remaining?: string[] }) =>
    api.post('/api/support/auto/release-notes', data),
  updateReleaseNote: (id: string, data: Record<string, unknown>) =>
    api.patch(`/api/support/auto/release-notes/${id}`, data),
  deleteReleaseNote: (id: string) => api.delete(`/api/support/auto/release-notes/${id}`),

  // Configuration
  getConfig: () => api.get('/api/support/auto/config'),
  updateConfig: (data: Record<string, unknown>) => api.put('/api/support/auto/config', data),

  // Reprocess failed
  reprocess: () => api.post('/api/support/auto/reprocess', {}),

  // Override (reclassify inline)
  override: (ticketId: string, field: string, value: string) =>
    api.post('/api/support/auto/override-inline', { ticket_id: ticketId, field, value }),

  // Test agent
  testAgent: (subject: string, message: string, platform?: string) =>
    api.post('/api/support/auto/test', { subject, message, platform }),

  // Approve & send (POST with optional edited_response)
  approve: (ticketId: string, token: string, editedResponse?: string) =>
    api.post(`/api/support/auto/approve?ticket=${encodeURIComponent(ticketId)}&token=${encodeURIComponent(token)}`, editedResponse ? { edited_response: editedResponse } : {}),

  // Get a single response record by ticket ID
  getResponseByTicket: (ticketId: string) => api.get(`/api/support/auto/responses?ticket_id=${encodeURIComponent(ticketId)}`),

  // Stats
  getStats: () => api.get('/api/support/auto/stats'),
};

export default supportService;
